import * as T from 'three';
import { AdvancedEditor } from './editor-advanced';
import { editorDefaults, physicsSignature } from './editor-model';
import { EditorView } from './editor-view';
import { analyzeRoute } from './route-analysis';
import { TRAVERSAL_TYPE_NAMES, DEAD_BRANCH, type WorldData, type SimState, type MoveInput, type Traversal, type Vec3 } from './types';
import { Simulation } from './simulation';
import { exportJSON, validateMap } from './storage';
export const escapeHTML = (v: unknown) => String(v).replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]!));
export class Editor {
    private view?: EditorView;
    private advanced: AdvancedEditor;
    revision = 0;
    onPlayActivity?: (id: string) => void;
    private trigger = '';
    private triggerTime = -1;
    private previous?: SimState;
    get testSpeed() { return this.advanced?.speed || 1; }
    edit(change: () => void) { return this.transaction(change); }
    refreshView() { this.refresh(); this.preview({ x: 0, z: 0, buttons: 0, assist: 1 }); }
    focus(p: Vec3) { this.view ? this.view.focus(p) : this.setCamera(p); }
    lookAt(position: Vec3, target: Vec3, fov: number) { this.view?.lookAt(position, target, fov); }
    selectObject(id: number) { this.selected = id; this.refreshView(); }
    futurePoint(): Vec3 { return this.trajectory[Math.min(this.trajectory.length - 1, Math.round(this.timeOffset * 20))] || this.sim.read().p; }
    ensureFrozen() { this.frozen = true; this.trigger = ''; this.apexArmed = false; this.refreshView(); }
    restoreState(raw: number[]) { this.sim.restore(raw); this.history = []; this.previous = undefined; this.ensureFrozen(); this.focus(this.sim.read().p); }
    teleport(p: Vec3) { this.sim.spawn(0, p); this.history = []; this.previous = undefined; this.ensureFrozen(); this.focus(p); }
    stepOnce() { if (!this.frozen)
        return; const input = { x: 0, z: 0, buttons: 0, assist: 1 }; this.sim.step(0, input); this.remember(this.sim.read(), input); this.refreshView(); }
    armTrigger(kind: string) { this.trigger = kind; this.triggerTime = -1; this.apexArmed = kind === 'apex'; this.frozen = false; this.clearDebug(); this.refresh(); this.notify('Freeze trigger armed.'); }
    rewindEvent(kind: 'grab' | 'release' | 'perch') {
        for (let i = this.history.length - 2; i > 0; i--) {
            const a = this.history[i - 1], b = this.history[i];
            if ((kind === 'grab' && b[16] >= 0 && a[16] !== b[16]) || (kind === 'release' && a[16] >= 0 && b[16] < 0) || (kind === 'perch' && a[15] !== 0 && b[15] === 0)) {
                this.sim.restore(b);
                this.history = this.history.slice(0, i + 1);
                this.ensureFrozen();
                this.notify(`Returned to the previous ${kind}.`);
                return;
            }
        }
        this.notify(`No previous ${kind} in the last 30 seconds.`);
    }
    attachView(camera: T.PerspectiveCamera, canvas: HTMLCanvasElement) {
        this.advanced.attach(camera);
        this.view = new EditorView(this.scene, camera, canvas, this.world, id => { this.selected = id; this.refresh(); this.preview({ x: 0, z: 0, buttons: 0, assist: 1 }); }, (id, a, b, r) => {
            const o = this.world.objects.find(o => o.id === id);
            if (o && !o.locked)
                this.transaction(() => { o.a = a; o.b = b; o.radius = r; });
        });
        document.querySelectorAll<HTMLElement>('[data-transform]').forEach(b => { b.setAttribute('aria-pressed', String(b.classList.contains('active'))); b.onclick = () => { this.view?.mode(b.dataset.transform as 'translate' | 'rotate' | 'scale'); document.querySelectorAll('[data-transform]').forEach(c => { c.classList.toggle('active', c === b); c.setAttribute('aria-pressed', String(c === b)); }); }; });
        document.getElementById('editor-snap')!.addEventListener('change', e => this.view?.snap((e.target as HTMLInputElement).checked));
    }
    update() { this.view?.update(); this.advanced.update(); }
    active = false;
    frozen = true;
    selected = -1;
    history: number[][] = [];
    debug = new T.Group();
    trajectory: Vec3[] = [];
    timeOffset = .5;
    private undoStack: string[] = [];
    private redoStack: string[] = [];
    private tick = 0;
    private recording: number[][] | null = null;
    private recordedInputs: MoveInput[] = [];
    private recordStart: number[] = [];
    private savedState: number[] = [];
    private showReach = true;
    apexArmed = false;
    constructor(public world: WorldData, private sim: Simulation, private scene: T.Scene, private rebuild: () => void, private notify: (s: string) => void, private setCamera: (p: Vec3) => void) { editorDefaults(world); scene.add(this.debug); this.debug.visible = false; this.bind(); this.advanced = new AdvancedEditor(this, sim, scene, notify); }
    get panel() { return document.getElementById('editor')!; }
    toggle(state: SimState, input: MoveInput) {
        this.trigger = '';
        this.apexArmed = false;
        this.active = !this.active;
        this.frozen = true;
        this.panel.hidden = !this.active;
        this.debug.visible = this.active;
        if (this.active) {
            this.savedState = state.raw.slice();
            this.refresh();
            this.preview(input);
        }
        else
            this.clearDebug();
        this.syncView();
    }
    remember(s: SimState, input: MoveInput) {
        if (this.recording)
            this.recordedInputs.push({ ...input });
        if (this.recordedInputs.length >= 36000 && this.recording)
            this.record();
        this.tick++;
        this.history.push(s.raw.slice());
        if (this.history.length > 3600)
            this.history.shift();
        if (this.tick % 6 === 0) {
            if (this.recording)
                this.recording.push(s.raw.slice());
        }
        const before = this.previous;
        if (this.active && !this.frozen && this.trigger) {
            if (this.trigger === 'release' && before && before.grab >= 0 && s.grab < 0)
                this.triggerTime = s.time + .2;
            const opened = before && before.candidate < 0 && s.candidate >= 0, closed = before && before.candidate >= 0 && s.candidate < 0;
            let collision = false;
            if (this.trigger === 'collision' && s.mode === 1 && s.v[1] < 0) {
                this.sim.restore(s.raw, 31);
                for (let tick = 0; tick < 12; tick++) {
                    this.sim.step(31, input);
                    if (this.sim.read(31).mode === 0) {
                        collision = true;
                        break;
                    }
                }
            }
            if ((this.trigger === 'apex' && s.v[1] <= 0 && s.mode === 1) || (this.trigger === 'release' && this.triggerTime >= 0 && s.time >= this.triggerTime) || (this.trigger === 'opens' && opened) || (this.trigger === 'closes' && closed) || collision) {
                this.trigger = '';
                this.apexArmed = false;
                this.frozen = true;
                this.refreshView();
                this.notify('Freeze trigger reached.');
            }
        }
        this.previous = s;
    }
    armApex() { this.armTrigger('apex'); }
    freeze(input: MoveInput) {
        this.trigger = '';
        this.apexArmed = false;
        this.frozen = !this.frozen;
        if (this.frozen)
            this.preview(input);
        else
            this.clearDebug();
        this.refresh();
    }
    rewind(seconds = 1) {
        this.trigger = '';
        this.apexArmed = false;
        this.previous = undefined;
        const index = Math.max(0, this.history.length - 1 - Math.round(seconds * 120));
        if (this.history[index]) {
            this.sim.restore(this.history[index]);
            this.history = this.history.slice(0, index + 1);
            this.notify(`Rewound ${seconds} second${seconds === 1 ? '' : 's'}.`);
        }
        else
            this.sim.restore(this.savedState);
        this.frozen = false;
        this.clearDebug();
        this.refresh();
    }
    private transaction(change: () => void): boolean {
        const before = JSON.stringify(this.world), signature = physicsSignature(this.world);
        try {
            change();
            const errors = validateMap(this.world);
            if (errors.length)
                throw Error(errors[0]);
        }
        catch (error) {
            Object.assign(this.world, JSON.parse(before));
            this.notify((error as Error).message);
            this.refresh();
            return false;
        }
        if (JSON.stringify(this.world) === before) {
            this.refreshView();
            return true;
        }
        this.undoStack.push(before);
        if (this.undoStack.length > 30)
            this.undoStack.shift();
        this.redoStack = [];
        if (physicsSignature(this.world) !== signature)
            for (const route of this.world.routes)
                if (route.proven)
                    route.stale = true;
        this.commit();
        return true;
    }
    private commit() {
        this.revision++;
        editorDefaults(this.world);
        const state = this.sim.read();
        const contact = this.world.objects.find(o => o.id === state.grab);
        if (state.grab >= 0) {
            let invalid = !contact;
            if (contact) {
                const p = new T.Vector3(...state.anchor), a = new T.Vector3(...contact.a), b = new T.Vector3(...contact.b), ab = b.clone().sub(a);
                const t = Math.max(0, Math.min(1, p.clone().sub(a).dot(ab) / Math.max(.0001, ab.lengthSq())));
                invalid = p.distanceTo(a.addScaledVector(ab, t)) > .8;
            }
            if (invalid) {
                state.raw[16] = -1;
                state.raw[15] = 1;
                state.raw[13] = .18;
                this.sim.restore(state.raw);
            }
        }
        this.world.physicsVersion = this.sim.version;
        this.sim.load(this.world);
        this.rebuild();
        this.refresh();
        try {
            localStorage.setItem('canopy.map.autosave', JSON.stringify(this.world));
            document.getElementById('editor-save-state')!.textContent = 'Saved on this device';
        }
        catch {
            this.notify('Device storage is full. Export your map to keep it.');
        }
        this.preview({ x: 0, z: 0, buttons: 0, assist: 1 });
    }
    undo() {
        const value = this.undoStack.pop();
        if (!value)
            return;
        this.redoStack.push(JSON.stringify(this.world));
        Object.assign(this.world, JSON.parse(value));
        this.commit();
    }
    redo() {
        const value = this.redoStack.pop();
        if (!value)
            return;
        this.undoStack.push(JSON.stringify(this.world));
        Object.assign(this.world, JSON.parse(value));
        this.commit();
    }
    place(kind: number) {
        if (!this.frozen) {
            this.notify('Freeze the route before placing a new object.');
            return;
        }
        const p = this.trajectory[Math.min(this.trajectory.length - 1, Math.round(this.timeOffset * 20))] || this.sim.read().p;
        const id = Math.max(0, ...this.world.objects.map(o => o.id)) + 1;
        const reach: Vec3 = [p[0], p[1] + 1.4, p[2]];
        const nearest = this.world.regions.reduce((a, b) => Math.hypot(a.center[0] - p[0], a.center[2] - p[2]) < Math.hypot(b.center[0] - p[0], b.center[2] - p[2]) ? a : b);
        this.transaction(() => {
            let a: Vec3 = [reach[0] - 1.6, reach[1], reach[2]], b: Vec3 = [reach[0] + 1.6, reach[1], reach[2] + .3], radius = .23;
            if (kind === 2) {
                a = [reach[0], reach[1] + 7, reach[2]];
                b = reach;
                radius = .06;
            }
            if (kind === 5) {
                a = [reach[0] - 1, -2, reach[2]];
                b = [reach[0] - 1, reach[1] + 5, reach[2]];
                radius = .6;
            }
            if (kind === 0 || kind === 4) {
                a = [p[0], p[1] - 2, p[2]];
                b = [...a];
                radius = 3;
            }
            this.world.objects.push({ id, uuid: crypto.randomUUID(), type: kind, a, b, radius, name: `${TRAVERSAL_TYPE_NAMES[kind]} ${id}`, tags: kind === DEAD_BRANCH ? ['authored', 'dead'] : ['authored'], region: nearest.id, art: kind === 0 ? 'deck' : undefined, locked: false });
            this.selected = id;
        });
        this.notify('Placed at the predicted reach point. Rewind to try it.');
    }
    checkpoint() { const s = this.sim.read(); this.transaction(() => { this.world.spawn = [...s.p]; }); this.notify('Map checkpoint placed.'); }
    record() {
        if (this.recording) {
            const trace = this.recording;
            this.recording = null;
            this.transaction(() => this.world.routes.push({ id: crypto.randomUUID(), name: `Recorded route ${this.world.routes.length + 1}`, points: trace.map(a => a.slice(0, 3) as Vec3), trace, proven: trace.length > 10 && trace.some(a => a[16] >= 0), physicsVersion: this.sim.version, stale: false, inputs: this.recordedInputs, initialState: this.recordStart, finalState: this.sim.read().raw }));
            this.notify(`Recorded ${trace.length} route samples.`);
        }
        else {
            this.recording = [];
            this.recordedInputs = [];
            this.recordStart = this.sim.read().raw.slice();
            this.frozen = false;
            this.notify('Recording your route. Press Record again to save.');
        }
        this.refresh();
    }
    validate() {
        const errors = validateMap(this.world);
        const warnings: string[] = [];
        for (const r of this.world.routes) {
            if (r.physicsVersion !== this.sim.version) {
                r.stale = true;
                warnings.push(`${r.name}: recorded with a different physics version.`);
                continue;
            }
            if (r.inputs && r.initialState && r.finalState) {
                const result = analyzeRoute(this.sim, r);
                const delta = result.error;
                r.stale = !result.exact;
                if (r.stale)
                    warnings.push(`${r.name}: replay differs by ${delta.toFixed(2)} m.`);
            }
            else if (r.stale)
                warnings.push(`${r.name}: geometry changed; replay needed.`);
        }
        if (!this.world.objects.some(o => (o.type === 0 || o.type === 1) && Math.hypot(o.a[0] - this.world.spawn[0], o.a[2] - this.world.spawn[2]) < o.radius + 3))
            warnings.push('Checkpoint needs a nearby recovery surface.');
        this.advanced.showIssues();
        document.getElementById('validation')!.textContent = [...errors, ...warnings, `${this.world.objects.length} objects · ${this.world.regions.length} regions · ${this.world.pois.length} POIs`, errors.length ? 'Fix errors before sharing.' : 'Map structure is valid. Authored routes still need playtesting.'].join('\n');
    }
    preview(input: MoveInput) {
        this.clearDebug();
        if (!this.active || !this.frozen)
            return;
        const s = this.sim.read();
        this.trajectory = this.sim.predict({ ...input, buttons: 0 }, 2.5);
        const line = new T.Line(new T.BufferGeometry().setFromPoints(this.trajectory.map(p => new T.Vector3(...p))), new T.LineBasicMaterial({ color: '#f6d475', depthTest: false }));
        line.renderOrder = 10;
        this.debug.add(line);
        if (this.showReach)
            for (const [r, color] of [[1.6, '#b6ebd2'], [1.95, '#edc46d'], [2.05, '#c0afdf']] as const) {
                const ball = new T.Mesh(new T.SphereGeometry(r, 16, 8), new T.MeshBasicMaterial({ color, wireframe: true, transparent: true, opacity: .16, depthTest: false }));
                ball.position.set(...s.p);
                this.debug.add(ball);
            }
        for (const o of this.world.objects) {
            if (o.type === 6 || Math.hypot(o.a[0] - s.p[0], o.a[1] - s.p[1], o.a[2] - s.p[2]) > 25)
                continue;
            const geometry = new T.BufferGeometry().setFromPoints([new T.Vector3(...o.a), new T.Vector3(...o.b)]);
            const l = new T.Line(geometry, new T.LineBasicMaterial({ color: o.id === this.selected ? '#ffdd72' : '#a1dec0', depthTest: false, transparent: true, opacity: o.id === this.selected ? 1 : .4 }));
            this.debug.add(l);
            if (o.id === this.selected) {
                const ball = new T.Mesh(new T.SphereGeometry(Math.max(.18, o.radius), 10, 6), new T.MeshBasicMaterial({ color: '#ffdd72', wireframe: true, depthTest: false }));
                ball.position.set(...o.a);
                this.debug.add(ball);
            }
        }
        const cursor = this.trajectory[Math.min(this.trajectory.length - 1, Math.round(this.timeOffset * 20))];
        if (cursor) {
            const ball = new T.Mesh(new T.SphereGeometry(.15, 12, 8), new T.MeshBasicMaterial({ color: '#fff3ab', depthTest: false }));
            ball.position.set(cursor[0], cursor[1] + 1.4, cursor[2]);
            this.debug.add(ball);
        }
        document.getElementById('editor-state')!.textContent = `${['Perched', 'In flight', 'Hand grab', 'Tail grab', 'Swimming', 'Scrambling'][s.mode]} · ${Math.hypot(...s.v).toFixed(1)} m/s · candidate ${s.candidate < 0 ? 'none' : s.candidate} · score ${s.score.toFixed(2)} · ${['clear catch', 'out of reach', 'blocked by trunk', 'recently released'][s.missReason] || 'searching'}`;
    }
    private clearDebug() {
        for (const o of [...this.debug.children]) {
            this.debug.remove(o);
            if (o instanceof T.Mesh || o instanceof T.Line) {
                o.geometry.dispose();
                (o.material as T.Material).dispose();
            }
        }
    }
    private syncView() { this.view?.sync(this.active, this.frozen, this.world.objects.find(o => o.id === this.selected), this.sim.read().p); }
    private refresh() {
        this.syncView();
        this.advanced?.refresh();
        const routes = document.getElementById('route-list') as HTMLSelectElement;
        const routeId = routes.value;
        routes.innerHTML = this.world.routes.filter(r => r.inputs?.length).map(r => `<option value="${escapeHTML(r.id)}">${escapeHTML(r.name)}</option>`).join('');
        if (Array.from(routes.options).some(o => o.value === routeId))
            routes.value = routeId;
        document.getElementById('editor-freeze')!.textContent = this.frozen ? '▶ Test route' : 'Ⅱ Freeze & build';
        document.getElementById('editor-record')!.textContent = this.recording ? '■ Save recorded route' : '● Record route';
        const select = document.getElementById('object-list') as HTMLSelectElement;
        select.innerHTML = this.world.objects.filter(o => o.type !== 6).map(o => `<option value="${o.id}">${escapeHTML(o.name)}</option>`).join('');
        select.value = String(this.selected);
        const o = this.world.objects.find(o => o.id === this.selected);
        document.getElementById('object-fields')!.hidden = !o;
        if (o) {
            for (const key of ['name', 'radius'] as const)
                (document.getElementById(`object-${key}`) as HTMLInputElement).value = String(o[key]);
            for (let i = 0; i < 3; i++)
                (document.getElementById(`object-${'xyz'[i]}`) as HTMLInputElement).value = o.a[i].toFixed(2);
            (document.getElementById('object-lock') as HTMLInputElement).checked = !!o.locked;
        }
    }
    private bind() {
        const on = (id: string, f: () => void) => document.getElementById(id)!.addEventListener('click', f);
        on('editor-undo', () => this.undo());
        on('editor-redo', () => this.redo());
        on('editor-rewind', () => this.rewind(1));
        on('editor-record', () => this.record());
        on('editor-checkpoint', () => this.checkpoint());
        on('editor-poi', () => this.advanced.open('poi'));
        on('editor-volume', () => this.advanced.open('volume'));
        on('editor-validate', () => this.validate());
        on('editor-analyze', () => {
            const id = (document.getElementById('route-list') as HTMLSelectElement).value, route = this.world.routes.find(r => r.id === id);
            if (!route)
                return;
            try {
                const r = analyzeRoute(this.sim, route, true);
                document.getElementById('validation')!.textContent = `${r.name}\nExact replay: ${r.exact ? 'pass' : 'changed'} · ${r.error.toFixed(3)} m error\nRelease/pump probes: ${r.successes} / ${r.trials} land near the recorded finish\nSuccessful tested release shift: ${r.releaseWindowMs.toFixed(0)} ms\nWorst finish deviation: ${r.maxDeviation.toFixed(2)} m\nThese probes measure this recording, not every possible route.`;
            }
            catch (e) {
                this.notify((e as Error).message);
            }
        });
        on('editor-export', () => exportJSON('canopy-map.json', this.world));
        on('editor-recover', () => {
            try {
                const value = JSON.parse(localStorage.getItem('canopy.map.autosave') || 'null');
                const errors = validateMap(value);
                if (errors.length)
                    throw Error(errors[0]);
                this.transaction(() => Object.assign(this.world, value));
                this.notify('Recovered the saved map.');
            }
            catch (e) {
                this.notify(`Recovery: ${(e as Error).message}`);
            }
        });
        document.querySelectorAll<HTMLElement>('[data-place]').forEach(b => b.onclick = () => this.place(Number(b.dataset.place)));
        document.getElementById('editor-time')!.addEventListener('input', e => { this.timeOffset = Number((e.target as HTMLInputElement).value); document.getElementById('editor-time-value')!.textContent = `+${this.timeOffset.toFixed(2)}s`; this.preview({ x: 0, z: 0, buttons: 0, assist: 1 }); });
        document.getElementById('object-list')!.addEventListener('change', e => { this.selected = Number((e.target as HTMLSelectElement).value); this.refresh(); this.preview({ x: 0, z: 0, buttons: 0, assist: 1 }); });
        on('object-focus', () => {
            const o = this.world.objects.find(o => o.id === this.selected);
            if (o)
                this.view ? this.view.focus(o.a) : this.setCamera(o.a);
        });
        on('object-delete', () => {
            const o = this.world.objects.find(o => o.id === this.selected);
            if (o?.locked) {
                this.notify('Unlock this contact before removing it.');
                return;
            }
            this.transaction(() => { this.world.objects = this.world.objects.filter(o => o.id !== this.selected); this.selected = -1; });
        });
        on('object-apply', () => {
            const o = this.world.objects.find(o => o.id === this.selected);
            if (!o)
                return;
            const values = ['x', 'y', 'z', 'radius'].map(k => Number((document.getElementById(`object-${k}`) as HTMLInputElement).value));
            if (values.some(n => !Number.isFinite(n)) || values[3] <= 0 || values[3] > 100) {
                this.notify('Enter finite coordinates and a radius between 0 and 100.');
                return;
            }
            this.transaction(() => {
                o.name = (document.getElementById('object-name') as HTMLInputElement).value.slice(0, 80);
                if (!o.locked) {
                    for (let i = 0; i < 3; i++) {
                        o.b[i] += values[i] - o.a[i];
                        o.a[i] = values[i];
                    }
                    o.radius = values[3];
                }
                o.locked = (document.getElementById('object-lock') as HTMLInputElement).checked;
            });
        });
        document.getElementById('editor-import')!.addEventListener('change', async (e) => {
            const file = (e.target as HTMLInputElement).files?.[0];
            if (!file)
                return;
            try {
                if (file.size > 4e6)
                    throw Error('Map exceeds 4 MB.');
                const value = JSON.parse(await file.text());
                const errors = validateMap(value);
                if (errors.length)
                    throw Error(errors.join(' '));
                this.transaction(() => Object.assign(this.world, value));
                this.notify('Map imported.');
            }
            catch (error) {
                this.notify(`Import failed: ${(error as Error).message}`);
            }
            (e.target as HTMLInputElement).value = '';
        });
    }
}
